import React, { useState, useCallback } from "react";
import { View, ScrollView } from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { SearchInput } from "@/components/ui/SearchInput";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/components/ui/StatCard";
import { Table, type TableColumn } from "@/components/ui/Table";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { Icon } from "@/components/ui/Icon";
import { Feather, Octicons } from "@expo/vector-icons";
import { usePagination } from "@/hooks/usePagination";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PageFooter } from "@/components/ui/PageFooter";
import { ROLE_LABELS, ROLE_VARIANTS } from "@/constants";
import { usersApi, type AdminUser } from "@/lib/api/users";
import { UserModal } from "../components/UserModal";

type UserTab = "active" | "inactive" | "disabled";

import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";

export default function UserListScreen() {
  const { t, locale } = useI18n();
  const router = useRouter();
  const { top } = useSafeAreaInsets();

  const { tab: tabParam } = useLocalSearchParams<{ tab?: UserTab }>();
  const activeTab: UserTab =
    tabParam === "inactive" || tabParam === "disabled" ? tabParam : "active";

  const { page, setPage, limit, resetPage, search, setSearch, debouncedSearch } = usePagination({
    initialPage: 1,
    initialLimit: 10,
  });

  const [userModalVisible, setUserModalVisible] = useState(false);

  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["users", "list", activeTab, debouncedSearch, limit],
    queryFn: ({ pageParam = 1 }) =>
      usersApi.list({
        tab: activeTab,
        search: debouncedSearch || undefined,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return lastPage.page * lastPage.pageSize < lastPage.total ? nextPage : undefined;
    },
    enabled: true,
  });

  const handleTabChange = useCallback(
    (tab: UserTab) => {
      router.setParams({ tab });
      resetPage();
    },
    [router, resetPage]
  );

  const total = data?.pages?.[0]?.total ?? 0;
  const activeCount = data?.pages?.[0]?.activeCount ?? 0;
  const inactiveCount = data?.pages?.[0]?.inactiveCount ?? 0;
  const disabledCount = data?.pages?.[0]?.disabledCount ?? 0;
  const items = data?.pages?.flatMap((p) => p.items) ?? [];

  const STATUS_VARIANT_MAP = {
    active: "filled-success",
    inactive: "filled-warning",
    disabled: "filled-error",
  } as const;

  const STATUS_LABEL_MAP = {
    active: "staff.users.tabs.active",
    inactive: "staff.users.tabs.inactive",
    disabled: "staff.users.tabs.disabled",
  } as const;

  const columns: TableColumn<AdminUser>[] = [
    {
      key: "name",
      header: t("staff.users.table.user"),
      flex: 1,
      renderCell: (item) => (
        <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
          {item.name}
        </AppText>
      ),
    },
    {
      key: "role",
      header: t("staff.users.table.role"),
      width: 220,
      renderCell: (item) => (
        <View className="items-start">
          <Badge variant={ROLE_VARIANTS[item.role]} label={ROLE_LABELS[item.role]} />
        </View>
      ),
    },
    {
      key: "status",
      header: t("staff.users.table.status"),
      width: 200,
      renderCell: (item) => (
        <View className="items-start">
          <Badge variant={STATUS_VARIANT_MAP[item.status]} label={STATUS_LABEL_MAP[item.status]} />
        </View>
      ),
    },
    {
      key: "lastActivity",
      header: t("staff.users.table.lastActivity"),
      width: 200,
      renderCell: (item) => {
        const { date, time } = formatSplitDateTime(item.lastActivityAt, locale);
        return (
          <>
            <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
              {formatRelativeTime(item.lastActivityAt, locale, t)}
            </AppText>
            <View className="flex-row items-center gap-1 mt-0.5">
              <AppText className="text-dark-text-20 text-sm">{date}</AppText>
              <View className="w-1 h-1 rounded-full bg-stroke-30" />
              <AppText className="text-dark-text-20 text-sm">{time}</AppText>
            </View>
          </>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: 160,
      renderCell: (item) => (
        <View className="items-end py-2">
          <Button
            label="common.view"
            variant="secondary"
            onPress={() => router.push(`/(staff)/users/${item.id}`)}
            className="py-2 h-[42px] border-stroke-30 rounded"
            labelClassName="hover:text-primary text-supporting-text"
          />
        </View>
      ),
    },
  ];

  return (
    <View className="flex-1 bg-stroke-10">
      <ScrollView
        className="flex-1 bg-white"
        testID="staff-users"
        contentContainerStyle={{ flexGrow: 1, paddingTop: top }}
      >
        {/* ── Page header ─────────────────────────────────────────── */}
        <View className="px-page-x tablet:px-20 pt-10 mb-10 items-start">
          <AppText
            weight="bold"
            className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("staff.users.title")}
          </AppText>
          <AppText className="text-base text-supporting-text mt-1">
            {t("staff.users.subtitle")}
          </AppText>

          {/* ── Stat cards ──────────────────────────────────────── */}
          <View className="items-start w-full flex-col tablet:flex-row tablet:flex-wrap tablet:max-w-4xl gap-4 mt-6">
            <StatCard
              icon={<Icon as={Octicons} name="person" size={18} className="text-green-50" />}
              iconBgClass="bg-green-5"
              value={activeCount}
              title={t("staff.users.stats.active")}
              description={t("staff.users.stats.activeDesc")}
              className="w-full"
            />
            <StatCard
              icon={<Icon as={Octicons} name="person" size={18} className="text-supporting-text" />}
              iconBgClass="bg-stroke-20"
              value={inactiveCount}
              title={t("staff.users.stats.inactive")}
              description={t("staff.users.stats.inactiveDesc")}
              className="w-full"
            />
            <StatCard
              icon={<Icon as={Octicons} name="person" size={18} className="text-error" />}
              iconBgClass="bg-error-light"
              value={disabledCount}
              title={t("staff.users.stats.disabled")}
              description={t("staff.users.stats.disabledDesc")}
              className="w-full"
            />
          </View>
        </View>

        {/* ── Table card ────────────────────────────────────────── */}
        <View className="w-full flex-col mb-10">
          <TabBarNavigation<UserTab>
            tabs={[
              { key: "active", label: t("staff.users.tabs.active"), count: activeCount },
              { key: "inactive", label: t("staff.users.tabs.inactive"), count: inactiveCount },
              { key: "disabled", label: t("staff.users.tabs.disabled"), count: disabledCount },
            ]}
            activeTab={activeTab}
            onTabPress={handleTabChange}
            contentContainerClassName="px-page-x tablet:px-20"
          />

          {/* Toolbar: Search + action buttons */}
          <View className="px-page-x tablet:px-20">
            <View className="gap-3 flex-wrap flex-row tablet:items-center justify-between py-4">
              <SearchInput
                containerClassName="max-w-sm w-full mb-0"
                value={search}
                onChangeText={setSearch}
                placeholder="common.search"
              />
              <View className="flex-row items-center gap-3">
                <Button
                  icon={<Icon as={Feather} name="plus" size={14} className="text-white" />}
                  label="staff.users.actions.addNewUser"
                  variant="primary"
                  onPress={() => setUserModalVisible(true)}
                />
              </View>
            </View>

            <View className="pb-6">
              <Table
                columns={columns}
                data={items}
                keyExtractor={(item) => item.id}
                minWidth={900}
                limit={data?.pages?.[0]?.pageSize ?? 10}
                totalCount={total}
                page={page}
                onPageChange={(p) => {
                  setPage(p);
                  if (p > (data?.pages?.length ?? 0) && hasNextPage) {
                    fetchNextPage();
                  }
                }}
                onRowPress={(item) => router.push(`/(staff)/users/${item.id}`)}
                emptyDescription="staff.users.empty"
                isLoading={isLoading}
              />
            </View>
          </View>
        </View>
        <PageFooter />
      </ScrollView>

      <UserModal
        visible={userModalVisible}
        onDismiss={() => setUserModalVisible(false)}
        onSuccess={() => setUserModalVisible(false)}
      />
    </View>
  );
}
